import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  deleteDailyMission,
  getDailyMissionsPayload,
  isDailyMissionsApiSuccess,
  listDailyMissions,
  updateDailyMission,
} from '../../services/dailyMissionsApi';
import {
  compareMissionsNewestFirst,
  dayStatus,
  deleteResultMessage,
  formatMissionDate,
  localToday,
  parseDayList,
  parseDeleteResult,
} from './dailyMissionUtils';
import { useDailyMissionsSession } from './useDailyMissionsSession';

const STATUS_CHIP = {
  today: { label: 'Today', color: 'primary' },
  upcoming: { label: 'Upcoming', color: 'info' },
  past: { label: 'Past', color: 'default' },
};

const PAGE_SIZE = 20;

function DailyMissionList() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const view = searchParams.get('from') === 'all' ? 'all' : 'upcoming';
  const offset = Math.max(0, Number(searchParams.get('offset') || 0) || 0);
  const [loading, setLoading] = useState(true);
  const [today, setToday] = useState(localToday());
  const serverTodayRef = useRef('');
  const [days, setDays] = useState([]);
  const [total, setTotal] = useState(0);
  const [todayMission, setTodayMission] = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const { handleApiResponse } = useDailyMissionsSession(setError);

  const setOffset = useCallback(
    (nextOffset) => {
      const params = new URLSearchParams(searchParams);
      if (nextOffset > 0) params.set('offset', String(nextOffset));
      else params.delete('offset');
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const loadDays = useCallback(
    async ({ silent = false } = {}) => {
      if (!silent) {
        setLoading(true);
        setError('');
      }
      try {
        const knownToday = serverTodayRef.current || localToday();
        const pageQuery = {
          limit: PAGE_SIZE,
          offset,
          ...(view === 'upcoming' ? { startDate: knownToday } : {}),
        };
        let result = await listDailyMissions(pageQuery);
        if (handleApiResponse(result, 'Failed to load daily missions.')) {
          if (!silent) {
            setDays([]);
            setTotal(0);
            setTodayMission(null);
          }
          return;
        }
        let parsed = parseDayList(getDailyMissionsPayload(result));
        if (view === 'upcoming' && knownToday !== parsed.today) {
          result = await listDailyMissions({ startDate: parsed.today, limit: PAGE_SIZE, offset });
          if (handleApiResponse(result, 'Failed to load daily missions.')) {
            if (!silent) {
              setDays([]);
              setTotal(0);
              setTodayMission(null);
            }
            return;
          }
          parsed = parseDayList(getDailyMissionsPayload(result));
        }
        if (parsed.total > 0 && offset >= parsed.total) {
          setOffset(Math.floor((parsed.total - 1) / PAGE_SIZE) * PAGE_SIZE);
          return;
        }
        serverTodayRef.current = parsed.today;
        setToday(parsed.today);
        setDays(parsed.days.slice().sort(compareMissionsNewestFirst));
        setTotal(parsed.total);

        const onPage = parsed.days.find((day) => day.mission_date === parsed.today) || null;
        let bannerMission = onPage;
        try {
          const todayResult = await listDailyMissions({
            startDate: parsed.today,
            endDate: parsed.today,
            limit: 1,
            offset: 0,
          });
          if (isDailyMissionsApiSuccess(todayResult)) {
            const todayPage = parseDayList(getDailyMissionsPayload(todayResult));
            bannerMission = todayPage.days.find((day) => day.mission_date === parsed.today) || null;
          }
        } catch {
          bannerMission = onPage;
        }
        setTodayMission(bannerMission);
      } catch (fetchError) {
        setError(fetchError.message || 'Failed to load daily missions.');
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [handleApiResponse, offset, setOffset, view]
  );

  useEffect(() => {
    loadDays();
  }, [loadDays]);

  useEffect(() => {
    const notice = location.state?.notice;
    if (!notice) return;
    setSnackbar({ open: true, message: notice, severity: 'success' });
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
  }, [location.pathname, location.search, location.state, navigate]);

  const handleToggleActive = async (day) => {
    setTogglingId(day.id);
    try {
      const result = await updateDailyMission(day.id, { is_active: !day.is_active });
      if (handleApiResponse(result, 'Failed to update this day.')) return;
      setSnackbar({
        open: true,
        message: day.is_active
          ? `${formatMissionDate(day.mission_date)} is off. Players who have not opened it see nothing. Players who already opened it keep their copy. The date stays occupied.`
          : `${formatMissionDate(day.mission_date)} is on.`,
        severity: 'success',
      });
      await loadDays({ silent: true });
    } catch (toggleError) {
      setSnackbar({ open: true, message: toggleError.message || 'Failed to update this day.', severity: 'error' });
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      const result = await deleteDailyMission(pendingDelete.id);
      if (handleApiResponse(result, 'Failed to delete this day.')) return;
      setSnackbar({
        open: true,
        message: deleteResultMessage(parseDeleteResult(getDailyMissionsPayload(result))),
        severity: 'success',
      });
      setPendingDelete(null);
      await loadDays({ silent: true });
    } catch (deleteError) {
      setSnackbar({ open: true, message: deleteError.message || 'Failed to delete this day.', severity: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2 }}>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={view}
          onChange={(_, next) => {
            if (!next) return;
            const params = new URLSearchParams(searchParams);
            params.set('from', next === 'all' ? 'all' : 'today');
            params.delete('offset');
            setSearchParams(params, { replace: true });
          }}
          aria-label="Which days to show"
        >
          <ToggleButton value="upcoming">Today &amp; upcoming</ToggleButton>
          <ToggleButton value="all">All days</ToggleButton>
        </ToggleButtonGroup>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton onClick={() => loadDays()} disabled={loading} title="Refresh">
            <RefreshIcon />
          </IconButton>
          <Button component={Link} to="/admin/daily-missions/analytics" variant="outlined">
            Analytics
          </Button>
          <Button component={Link} to="/admin/daily-missions/new" variant="contained" startIcon={<AddIcon />}>
            Add day
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && (
        <Alert severity={todayMission?.is_active ? 'success' : 'warning'} sx={{ mb: 2 }}>
          {todayMission?.is_active
            ? `Today (${formatMissionDate(today)}) is set: “${todayMission.title}” with ${todayMission.tasks?.length || 0} tasks.`
            : todayMission
              ? `Today (${formatMissionDate(today)}) has a mission, but it is turned off. The app shows nothing until you turn it on.`
              : `No active mission for today (${formatMissionDate(today)}). The app shows nothing until you add one.`}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Mission</TableCell>
                <TableCell>Tasks</TableCell>
                <TableCell>Reward</TableCell>
                <TableCell title="Players and guests who have opened this day">Players</TableCell>
                <TableCell>Active</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {days.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    {view === 'upcoming' ? 'No days scheduled from today onwards.' : 'No daily missions yet.'}
                  </TableCell>
                </TableRow>
              ) : (
                days.map((day) => {
                  const status = dayStatus(day.mission_date, today);
                  const chip = STATUS_CHIP[status];
                  return (
                    <TableRow key={day.id} hover sx={{ opacity: day.is_active ? 1 : 0.6 }}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Typography variant="body2" fontWeight={600}>
                          {formatMissionDate(day.mission_date)}
                        </Typography>
                        <Chip size="small" label={chip.label} color={chip.color} sx={{ mt: 0.5 }} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {day.title}
                        </Typography>
                        {day.subtitle ? (
                          <Typography variant="caption" color="text.secondary">
                            {day.subtitle}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        {(day.tasks || []).map((task, index) => (
                          <Typography key={task.id || index} variant="body2">
                            {index + 1}. {task.title}
                          </Typography>
                        ))}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{day.fan_energy_reward ?? 0} FE</TableCell>
                      <TableCell>{day.assigned_count ?? 0}</TableCell>
                      <TableCell>
                        <Switch
                          checked={!!day.is_active}
                          disabled={togglingId === day.id}
                          onChange={() => handleToggleActive(day)}
                          size="small"
                          inputProps={{ 'aria-label': `Active ${day.mission_date}` }}
                        />
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        <Button
                          component={Link}
                          to={`/admin/daily-missions/${day.id}`}
                          size="small"
                          startIcon={<EditIcon />}
                        >
                          Edit
                        </Button>
                        <Button
                          component={Link}
                          to={`/admin/daily-missions/new?copy=${day.id}`}
                          size="small"
                          startIcon={<ContentCopyIcon />}
                        >
                          Copy
                        </Button>
                        <IconButton
                          size="small"
                          aria-label={`Delete ${day.mission_date}`}
                          onClick={() => setPendingDelete(day)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!loading && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {total === 0 ? '0 days' : `${offset + 1}–${Math.min(offset + days.length, total)} of ${total}`}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button disabled={offset <= 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>
              Previous
            </Button>
            <Button disabled={offset + PAGE_SIZE >= total} onClick={() => setOffset(offset + PAGE_SIZE)}>
              Next
            </Button>
          </Box>
        </Box>
      )}

      <Dialog open={!!pendingDelete} onClose={() => !deleting && setPendingDelete(null)}>
        <DialogTitle>Delete this day?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {pendingDelete
              ? `The mission for ${formatMissionDate(pendingDelete.mission_date)} is deleted if nobody has opened it. If players already have it, it is deactivated instead and their progress stays.`
              : ''}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingDelete(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default DailyMissionList;
